import { Position, type Edge, type Node } from "reactflow";
import type { DataVaultModel, LayoutMap } from "../model/types";
import type { VaultNodeData } from "../components/types";

const CANVAS_FALLBACK_WIDTH = 360;
const HUB_VERTICAL_OFFSET = 0;
const SATELLITE_VERTICAL_OFFSET = 220;
const SATELLITE_COLUMN_OFFSET = 240;
const SATELLITE_ROW_GAP = 140;
const DEFAULT_SOURCE_POSITION = Position.Right;
const DEFAULT_TARGET_POSITION = Position.Left;

export type FlowNode = Node<VaultNodeData>;
export type VaultRelationshipEdgeData =
  | {
      relationshipType: "hub-satellite";
      satMemberId: number | null;
    }
  | {
      relationshipType: "link-hub";
      linkMemberId: number | null;
      linkId: string;
    }
  | {
      relationshipType: "link-satellite";
      linkId: string;
    };
export type FlowEdge = Edge<VaultRelationshipEdgeData>;

export function mergeNodes(
  draftNodes: FlowNode[],
  previousNodes: FlowNode[],
  savedLayout: LayoutMap
): FlowNode[] {
  const previousMap = new Map(previousNodes.map((node) => [node.id, node]));
  return draftNodes.map((node) => {
    const previous = previousMap.get(node.id);
    if (previous) {
      return {
        ...node,
        position: previous.position,
        positionAbsolute: previous.positionAbsolute ?? node.position
      };
    }
    const savedPosition = savedLayout[node.id];
    if (savedPosition) {
      return {
        ...node,
        position: savedPosition
      };
    }
    return node;
  });
}

export function generateFlowElements(
  model: DataVaultModel,
  previousNodes: FlowNode[],
  savedLayout: LayoutMap,
  selectedLinkId: string | null,
  selectedNodeId: string | null,
  onSelectNode: (id: string) => void,
  onSelectLink: (id: string) => void
): { nodes: FlowNode[]; edges: FlowEdge[] } {
  const nodes: FlowNode[] = [];
  const edges: FlowEdge[] = [];

  const resolvePosition = (id: string, fallback: { x: number; y: number }) => {
    const previous = previousNodes.find((node) => node.id === id);
    if (previous) {
      return previous.position;
    }
    return savedLayout[id] ?? fallback;
  };

  const hubPositions = new Map<string, { x: number; y: number }>();
  const satellitePositions = new Map<string, { x: number; y: number }>();
  const linkPositions = new Map<string, { x: number; y: number }>();

  model.hubs.forEach((hub, index) => {
    const position = resolvePosition(hub.id, {
      x: index * CANVAS_FALLBACK_WIDTH,
      y: HUB_VERTICAL_OFFSET
    });

    hubPositions.set(hub.id, position);
    nodes.push({
      id: hub.id,
      type: "hubNode",
      position,
      sourcePosition: DEFAULT_SOURCE_POSITION,
      targetPosition: DEFAULT_TARGET_POSITION,
      data: { entity: hub, onSelect: onSelectNode },
      selected: hub.id === selectedNodeId
    });
  });

  model.satellites.forEach((satellite, index) => {
    const parentPosition = satellite.parentHubId
      ? hubPositions.get(satellite.parentHubId)
      : undefined;
    const fallbackHubIndex = Math.max(
      0,
      model.hubs.findIndex((hub) => hub.id === satellite.parentHubId)
    );

    const fallback = parentPosition
      ? parentPosition
      : {
          x: fallbackHubIndex * CANVAS_FALLBACK_WIDTH,
          y: HUB_VERTICAL_OFFSET
        };

    const column = index % 2;
    const row = Math.floor(index / 2);

    const position = resolvePosition(satellite.id, {
      x:
        fallback.x +
        (column === 0 ? -SATELLITE_COLUMN_OFFSET : SATELLITE_COLUMN_OFFSET),
      y: fallback.y + SATELLITE_VERTICAL_OFFSET + row * SATELLITE_ROW_GAP
    });

    nodes.push({
      id: satellite.id,
      type: "satelliteNode",
      position,
      sourcePosition: DEFAULT_SOURCE_POSITION,
      targetPosition: DEFAULT_TARGET_POSITION,
      data: { entity: satellite, onSelect: onSelectNode },
      selected: satellite.id === selectedNodeId
    });

    satellitePositions.set(satellite.id, position);

    if (satellite.parentHubId) {
      const parentPosition = hubPositions.get(satellite.parentHubId);
      const satelliteIsLeftOfHub =
        parentPosition && position ? position.x < parentPosition.x : false;
      const sourceHandle = satelliteIsLeftOfHub ? "hub-source-left" : "hub-source-right";
      const targetHandle = satelliteIsLeftOfHub ? "sat-target-right" : "sat-target-left";

      const satMemberId = satellite.satMemberId ?? null;
      edges.push({
        id: `edge-${satellite.parentHubId}-${satellite.id}`,
        source: satellite.parentHubId,
        target: satellite.id,
        sourceHandle,
        targetHandle,
        type: "smoothstep",
        style: {
          stroke: "#22c55e",
          strokeWidth: 2
        },
        selected: false,
        data: {
          relationshipType: "hub-satellite",
          satMemberId
        }
      });
    }
  });

  model.manual.forEach((manualEntity, index) => {
    const position = resolvePosition(manualEntity.id, {
      x: index * CANVAS_FALLBACK_WIDTH,
      y: HUB_VERTICAL_OFFSET + SATELLITE_VERTICAL_OFFSET
    });
    nodes.push({
      id: manualEntity.id,
      type: "manualNode",
      position,
      sourcePosition: DEFAULT_SOURCE_POSITION,
      targetPosition: DEFAULT_TARGET_POSITION,
      data: { entity: manualEntity, onSelect: onSelectNode },
      selected: manualEntity.id === selectedNodeId
    });
  });

  model.links.forEach((link, index) => {
    const connectedHubPositions = link.connectedHubIds
      .map((hubId) => hubPositions.get(hubId))
      .filter((position): position is { x: number; y: number } => Boolean(position));

    const fallback = (() => {
      if (connectedHubPositions.length) {
        const avgX =
          connectedHubPositions.reduce((acc, position) => acc + position.x, 0) /
          connectedHubPositions.length;
        const avgY =
          connectedHubPositions.reduce((acc, position) => acc + position.y, 0) /
            connectedHubPositions.length - 180;
        return { x: avgX, y: avgY };
      }
      return {
        x: index * CANVAS_FALLBACK_WIDTH,
        y: HUB_VERTICAL_OFFSET - 180
      };
    })();

    const position = resolvePosition(link.id, fallback);

    nodes.push({
      id: link.id,
      type: "linkNode",
      position,
      sourcePosition: DEFAULT_SOURCE_POSITION,
      targetPosition: DEFAULT_TARGET_POSITION,
      data: { entity: link, onSelect: onSelectLink },
      selected: link.id === selectedLinkId
    });
    linkPositions.set(link.id, position);

    const isSelected = link.id === selectedLinkId;

    link.connectedHubIds.forEach((hubId) => {
      if (!hubPositions.has(hubId)) {
        return;
      }
      const hubPosition = hubPositions.get(hubId);
      if (!hubPosition) {
        return;
      }
      const linkPosition = linkPositions.get(link.id) ?? position;
      const linkIsAboveHub = linkPosition.y <= hubPosition.y;
      const sourceHandle = linkIsAboveHub ? "link-source-bottom" : "link-source-top";
      const targetHandle = linkIsAboveHub ? "hub-target-top" : "hub-target-bottom";

      const linkMemberId = link.linkMemberIdsByHubId?.[hubId] ?? null;
      edges.push({
        id: `edge-${link.id}-${hubId}`,
        source: link.id,
        target: hubId,
        sourceHandle,
        targetHandle,
        type: "smoothstep",
        animated: isSelected,
        style: {
          stroke: isSelected ? "#ef4444" : "#f87171",
          strokeWidth: isSelected ? 3 : 2
        },
        selected: isSelected,
        data: {
          relationshipType: "link-hub",
          linkMemberId,
          linkId: link.id
        }
      });
    });

    link.satelliteIds.forEach((satelliteId) => {
      if (!satellitePositions.has(satelliteId)) {
        return;
      }
      const satellitePosition = satellitePositions.get(satelliteId);
      if (!satellitePosition) {
        return;
      }
      const linkPosition = linkPositions.get(link.id) ?? position;
      const satelliteIsLeftOfLink = satellitePosition.x < linkPosition.x;
      const sourceHandle = satelliteIsLeftOfLink ? "link-source-left" : "link-source-right";
      const targetHandle = satelliteIsLeftOfLink ? "sat-target-right" : "sat-target-left";

      edges.push({
        id: `edge-${link.id}-${satelliteId}`,
        source: link.id,
        target: satelliteId,
        sourceHandle,
        targetHandle,
        type: "smoothstep",
        style: {
          stroke: "#f59e0b",
          strokeWidth: 2,
          strokeDasharray: "6 4"
        },
        selected: false,
        data: {
          relationshipType: "link-satellite",
          linkId: link.id
        }
      });
    });
  });

  return { nodes, edges };
}

export function getEntityById(
  model: DataVaultModel,
  id: string
): { type: "hub" | "satellite" | "link" | "manual"; entity: DataVaultModel["hubs"][number] | DataVaultModel["satellites"][number] | DataVaultModel["links"][number] | DataVaultModel["manual"][number] } | null {
  const hub = model.hubs.find((candidate) => candidate.id === id);
  if (hub) {
    return { type: "hub", entity: hub };
  }
  const satellite = model.satellites.find((candidate) => candidate.id === id);
  if (satellite) {
    return { type: "satellite", entity: satellite };
  }
  const link = model.links.find((candidate) => candidate.id === id);
  if (link) {
    return { type: "link", entity: link };
  }
  const manualEntity = model.manual.find((candidate) => candidate.id === id);
  if (manualEntity) {
    return { type: "manual", entity: manualEntity };
  }
  return null;
}
